/**
 * level4Session.ts
 * Per-level session helpers for World 4 — Crystal Tide Ruins (greybox).
 *
 * Thin config layer over {@link createWorldSession}. Four main stages
 * plus the PrismLeviathan boss arena (4-B) and a secret stage (4-0).
 * The 4-B completion path persists the air-dash ability unlock so World 5
 * becomes selectable on the world map (the W5 unlock predicate in
 * `worldUnlock.ts` keys on `levels['4-B'].completed`).
 *
 * The named exports below preserve the original public surface so the
 * dispatcher and tests are untouched; the shared bodies live in
 * `worldSessionFactory.ts`.
 *
 * @module level4Session
 */

import {
  createWorldSession,
  type WorldSession,
  type WorldSessionConfig,
} from './worldSessionFactory';
import type { PlayScene } from '../scenes/PlayScene';
import { persistDashUnlock } from '../services/abilityUnlocks';

/** Shape returned by {@link getWorld4Session} — alias of the shared bundle. */
export type World4Session = WorldSession;

// DECISION: the id→number and next-chain maps stay explicit (same shape
// as W3) so the factory's hasOwnProperty guards keep rejecting prototype
// keys. Secret stage 4-0 → integer 0, boss arena 4-B → integer 5, matching
// the LevelRegistry convention.
const CONFIG: WorldSessionConfig = {
  worldId: 4,
  worldLabel: 'World 4',
  idToLevelNumber: {
    '4-0': 0,
    '4-1': 1,
    '4-2': 2,
    '4-3': 3,
    '4-4': 4,
    '4-B': 5,
  },
  fallbackId: '4-1',
  nextChain: {
    '4-1': '4-2',
    '4-2': '4-3',
    '4-3': '4-4',
    '4-4': '4-B',
    // 4-B and the secret stage 4-0 are absent → resolve to `null` (bounce
    // back to the world map).
  },
  // DECISION: lazy `() => persistDashUnlock()` rather than capturing the
  // reference directly, so `jest.spyOn(abilityUnlocks, 'persistDashUnlock')`
  // in the race-regression test still intercepts the call (the arrow
  // resolves the namespace binding at call time, not config-build time).
  abilityUnlock: { bossId: '4-B', persist: () => persistDashUnlock() },
};

const helpers = createWorldSession(CONFIG);

/**
 * Resolve a `?level=` query value to a canonical World 4 session id.
 * Falls back to `'4-1'` for any unknown or missing input.
 *
 * @param raw - Raw param value from the router.
 * @returns A valid session id string.
 */
export function resolveWorld4LevelId(raw: string | string[] | undefined): string {
  return helpers.resolveLevelId(raw);
}

/**
 * Build a session bundle for the given World 4 level id.
 *
 * @param levelId - Canonical id (`'4-0'`, `'4-1'`..`'4-4'`, or `'4-B'`).
 * @returns A World4Session with everything `scene.init` needs.
 * @throws If the level id is not registered.
 */
export function getWorld4Session(levelId: string): World4Session {
  return helpers.getSession(levelId);
}

/**
 * Initialise the PlayScene from a World 4 session bundle. No finale
 * wiring — W4 stages exit on right-edge collision.
 *
 * @param scene - The PlayScene to initialise.
 * @param session - The session bundle returned by {@link getWorld4Session}.
 * @param skipCheckpointRestore - When true, respawn at the level start.
 */
export function initWorld4Scene(
  scene: PlayScene,
  session: World4Session,
  skipCheckpointRestore = false,
): void {
  helpers.initScene(scene, session, skipCheckpointRestore);
}

/**
 * Return the next World 4 level id in normal-progression order, or
 * `null` if the player is at the last shipped stage.
 *
 * @param levelId - Current level id.
 * @returns The next level id, or `null` if there is none.
 */
export function getNextWorld4LevelId(levelId: string): string | null {
  return helpers.getNextLevelId(levelId);
}

/**
 * Persist the just-completed World 4 level's collected ids and score.
 * Fire-and-forget: failures are logged, never thrown. When the boss
 * arena (`'4-B'`) is the completed level, also fires {@link persistDashUnlock}
 * (snapshot-before-await race guard lives in the factory).
 *
 * @param levelId - Save key for the completed level.
 */
export async function persistWorld4Completion(levelId: string): Promise<void> {
  await helpers.persistCompletion(levelId);
}
